import { useState } from 'react';
import Axios from 'axios';
import { useAppSettings } from '../hooks/useAppSettings';

interface DownloadFileParams {
    category: string;
    fileName: string;
}

export const useDownloadFile = () => {
    const { settings: appSettings } = useAppSettings();
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);

    const downloadFile = async ({ category, fileName }: DownloadFileParams): Promise<void> => {
        if (!appSettings?.api) {
            throw new Error('API settings not available');
        }

        setIsDownloading(true);
        setDownloadError(null);

        try {
            // Use Axios so the MSAL interceptor acquires a fresh token automatically
            const response = await Axios.get(
                `${appSettings.api}DownloadFile?category=${encodeURIComponent(category)}&fileName=${encodeURIComponent(fileName)}`,
                { responseType: 'blob' }
            );

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error: any) {
            const message = error.response?.statusText || error.message || 'Failed to download file';
            setDownloadError(message);
            throw new Error(`Download failed: ${message}`);
        } finally {
            setIsDownloading(false);
        }
    };

    const getFileUrl = (category: string, fileName: string): string => {
        if (!appSettings?.api || !fileName) return '';
        return `${appSettings.api}DownloadFile?category=${encodeURIComponent(category)}&fileName=${encodeURIComponent(fileName)}`;
    };

    return {
        downloadFile,
        getFileUrl,
        isDownloading,
        downloadError,
    };
};
